import {
  type LabFit,
  type LabModel,
  type LabModelId,
  type LabSim,
  PHET_SITE,
  fitLabel,
  labSubjects,
  modelsFor,
  simImage,
  simsFor,
} from '@shkola/api';
import { type SubjectIcon, subjectLook } from '@shkola/design';
import { Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentContext } from '../app/ViewContext';
import {
  Block,
  GRAD,
  type Grad,
  INK,
  INK_FAINT,
  INK_SOFT,
  PAPER,
  PageHead,
  SUBJECT_GRAD,
  SubjectArt,
  TaskArt,
  Tabs,
  grad,
  ringSmall,
} from '../design/forms';
import { ArrowUpRightIcon } from '../design/icons';

/*
 * «Лабораторія» in «Форми 2» (28.10): the title on the paper with what it is under it and the choice of
 * simulations or 3D models beside it; the subject and the class as two pills; the items as white cards on
 * the paper — the picture of a simulation in a soft frame (a model: its subject's line picture on its
 * gradient), the name light, what it shows, where it fits as soft labels in the subjects' colours, ↗.
 */

/**
 * The page's title with its controls: beside the title on a wide screen, in a row under it on a phone
 * (beside the big title they would squeeze it).
 */
function TitleRow({ children, ...head }: Parameters<typeof PageHead>[0]) {
  return (
    <div className="flex flex-col gap-3">
      <PageHead {...head}>
        {children && (
          <span className="hidden flex-wrap items-center gap-2 lg:flex">{children}</span>
        )}
      </PageHead>
      {children && <div className="flex flex-wrap items-center gap-2 lg:hidden">{children}</div>}
    </div>
  );
}

export type LabTab = 'sims' | 'models';

/** Where an item fits: a soft label in the subject's colour — «Фізика · 7 клас». */
export function FitChips({ fits }: { fits: readonly LabFit[] }) {
  return (
    <span className="flex flex-wrap gap-1">
      {fits.map((f) => {
        const tone = SUBJECT_GRAD[subjectLook(f[0]).tone];
        return (
          <span
            key={f[0]}
            className="inline-flex h-6 items-center rounded-[7px] px-2 text-[12px] leading-none font-medium whitespace-nowrap"
            style={{ backgroundImage: grad(tone), color: GRAD[tone].ink }}
          >
            {f[0]} · {fitLabel(f)}
          </span>
        );
      })}
    </span>
  );
}

/** A model has no screenshot: its subject's line picture on that subject's gradient. */
export const MODEL_ART: Record<LabModelId, { icon: SubjectIcon; tone: Grad }> = {
  'solar-system': { icon: 'Earth', tone: 'sky' },
  saturn: { icon: 'Telescope', tone: 'lavender' },
  molecules: { icon: 'FlaskConical', tone: 'aqua' },
  atom: { icon: 'Atom', tone: 'cobalt' },
  solids: { icon: 'Triangle', tone: 'butter' },
  cell: { icon: 'Leaf', tone: 'mint' },
};

const card =
  'group flex min-w-0 flex-col rounded-[24px] bg-white p-2 transition-transform duration-300 ease-spring hover:-translate-y-0.5';

/** The words of a card: the name light with ↗, what it shows, where it fits. */
function CardText({
  title,
  about,
  fits,
}: {
  title: string;
  about: string;
  fits: readonly LabFit[];
}) {
  return (
    <span className="flex flex-1 flex-col gap-1.5 px-3 pt-3 pb-2.5">
      <span className="flex items-start gap-3">
        <span
          className="min-w-0 flex-1 font-display text-[21px] leading-[27px] font-light tracking-[-0.02em]"
          style={{ color: INK }}
        >
          {title}
        </span>
        <span
          aria-hidden
          className={`${ringSmall} group-hover:bg-[var(--sh-dark)] group-hover:text-white`}
        >
          <ArrowUpRightIcon size={13} weight="bold" />
        </span>
      </span>
      <span className="text-[14px] leading-[20px]" style={{ color: INK_SOFT }}>
        {about}
      </span>
      <span className="mt-auto pt-2">
        <FitChips fits={fits} />
      </span>
    </span>
  );
}

function SimCard({ sim }: { sim: LabSim }) {
  return (
    <Link to="/lab/sim/$slug" params={{ slug: sim.slug }} className={card}>
      <span
        className="block aspect-[8/5] overflow-hidden rounded-[18px]"
        style={{ backgroundColor: PAPER }}
      >
        <img
          src={simImage(sim.slug)}
          alt=""
          loading="lazy"
          className="size-full object-cover transition duration-300 group-hover:scale-[1.03]"
        />
      </span>
      <CardText title={sim.title} about={sim.about} fits={sim.fits} />
    </Link>
  );
}

function ModelCard({ model }: { model: LabModel }) {
  const art = MODEL_ART[model.id];
  return (
    <Link to="/lab/model/$modelId" params={{ modelId: model.id }} className={card}>
      <span
        className="relative isolate flex aspect-[8/5] items-center justify-center overflow-hidden rounded-[18px]"
        style={{ backgroundImage: grad(art.tone) }}
      >
        <SubjectArt
          icon={art.icon}
          tone={art.tone}
          size={112}
          className="transition-transform duration-500 ease-spring group-hover:scale-105 group-hover:-rotate-3"
        />
      </span>
      <CardText title={model.title} about={model.about} fits={model.fits} />
    </Link>
  );
}

const selectClass =
  'h-10 max-w-full rounded-full bg-white px-4 text-[14px] font-medium text-[var(--sh-ink)] outline-none focus:ring-2 focus:ring-[var(--sh-dark)]';

/**
 * «Лабораторія»: simulations of PhET in Ukrainian and our 3D models, by subject and class. A
 * student starts with their own class; anyone can look wider.
 */
export function LabPage({ tab }: { tab: LabTab }) {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  const navigate = useNavigate();
  const own =
    ctx.kind === 'student' || ctx.kind === 'guardian' ? ctx.child.class?.grade : undefined;
  const [subject, setSubject] = useState('');
  const [grade, setGrade] = useState<number | ''>(own ?? '');
  const filter = {
    ...(subject && { subject }),
    ...(grade !== '' && { grade }),
  };
  const sims = simsFor(filter);
  const models = modelsFor(filter);
  const shown = tab === 'sims' ? sims.length : models.length;

  return (
    <div className="flex flex-col gap-4">
      <TitleRow title={t('lab.title')} sub={t('lab.intro')}>
        <Tabs
          label={t('lab.title')}
          value={tab}
          onChange={(key) => void navigate({ to: '/lab', search: { tab: key } })}
          options={(['sims', 'models'] as const).map((key) => ({
            id: key,
            label: t(`lab.${key}`),
          }))}
        />
      </TitleRow>

      <div className="flex flex-wrap items-center gap-2">
        <select
          aria-label={t('lab.allSubjects')}
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className={selectClass}
        >
          <option value="">{t('lab.allSubjects')}</option>
          {labSubjects(grade === '' ? undefined : grade).map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select
          aria-label={t('lab.allClasses')}
          value={grade}
          onChange={(e) => {
            setGrade(e.target.value === '' ? '' : Number(e.target.value));
            setSubject('');
          }}
          className={selectClass}
        >
          <option value="">{t('lab.allClasses')}</option>
          {Array.from({ length: 11 }, (_, i) => i + 1).map((n) => (
            <option key={n} value={n}>
              {t('lab.grade', { n })}
            </option>
          ))}
        </select>
      </div>

      {shown === 0 ? (
        <Block>
          <div className="flex flex-col items-center py-12 text-center">
            <TaskArt kind="soon" tone="lavender" size={88} />
            <p className="mt-3 max-w-md text-[15px]" style={{ color: INK_SOFT }}>
              {t('lab.empty')}
            </p>
          </div>
        </Block>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {tab === 'sims'
            ? sims.map((s) => <SimCard key={s.slug} sim={s} />)
            : models.map((m) => <ModelCard key={m.id} model={m} />)}
        </div>
      )}

      {tab === 'sims' && (
        <p className="px-1 text-[12.5px] leading-[18px]" style={{ color: INK_FAINT }}>
          {t('lab.credit')}{' '}
          <a href={PHET_SITE} target="_blank" rel="noreferrer" className="underline">
            phet.colorado.edu
          </a>
        </p>
      )}
    </div>
  );
}
